/**
 * SETTINGS HUB CATALOG — the single source of truth for the Settings landing page.
 *
 * The hub (settings.html, `activeTab === 'hub'`) renders these categories as card
 * sections: icon + title + one-line description, click → the setting itself. Cards
 * carry the SAME pro-mode flags the submenu items used, so Pro tenants and
 * enterprise tenants each see exactly the set they can actually open:
 *
 *  - `proHidden` — hidden when the tenant runs in Pro (single-journey) mode
 *  - `searchOnly` — hidden from the grid, but still found by the search box
 *  - `proOnly`   — shown ONLY in Pro mode (e.g. Stages / guided Automation)
 *
 * The hub itself is admin-only: non-admins never see it (Settings hard-routes them
 * to ?tab=profile), so cards don't need per-role flags.
 *
 * `keywords` feed the hub search along with title + description — put the words an
 * admin would actually type there ("logo", "webhook", "password"), not synonyms of
 * the title.
 */

export interface SettingsCard {
    title: string;
    description: string;
    /** PrimeIcons class, e.g. 'pi pi-building'. */
    icon: string;
    /** Router target — either ['/settings'] + queryParams, or a standalone route. */
    routerLink: string[];
    queryParams?: Record<string, string>;
    /** Extra search terms beyond title/description. */
    keywords?: string[];
    proHidden?: boolean;
    proOnly?: boolean;
    /**
     * Kept out of the card grid but still reachable by searching for it. For a screen that is a
     * sub-part of another card's flow rather than its own starting point -- showing both as
     * top-level cards implies two integrations where there is one.
     */
    searchOnly?: boolean;
}

export interface SettingsCategory {
    label: string;
    /** Small PrimeIcons class shown next to the section label. */
    icon: string;
    cards: SettingsCard[];
}

export const SETTINGS_CATALOG: SettingsCategory[] = [
    {
        label: 'Organization',
        icon: 'pi pi-building',
        cards: [
            {
                title: 'Company Profile', icon: 'pi pi-building',
                description: 'Name, logo, brand colors & timezone',
                routerLink: ['/settings'], queryParams: { tab: 'company' },
                keywords: ['logo', 'brand', 'timezone', 'currency', 'name', 'vertical'],
            },
            {
                title: 'Billing & Wallet', icon: 'pi pi-credit-card',
                description: 'Plan, wallet balance & message usage',
                routerLink: ['/settings'], queryParams: { tab: 'billing' },
                keywords: ['subscription', 'plan', 'credits', 'payment', 'recharge'],
            },
            {
                title: 'Mobile App Config', icon: 'pi pi-mobile',
                description: 'Field-app features, tracking & attendance',
                routerLink: ['/mobile-app-config'],
                keywords: ['flutter', 'field', 'gps', 'attendance', 'app'],
            },
            {
                title: 'API Keys', icon: 'pi pi-key',
                description: 'Keys for the public API & journey API calls',
                routerLink: ['/settings'], queryParams: { tab: 'api' },
                keywords: ['token', 'integration', 'x-api-key', 'developer'],
            },
        ],
    },
    {
        label: 'People & Access',
        icon: 'pi pi-users',
        cards: [
            {
                title: 'Users', icon: 'pi pi-user',
                description: 'Invite and manage team members',
                routerLink: ['/settings'], queryParams: { tab: 'userslist' },
                keywords: ['invite', 'member', 'agent', 'password', 'deactivate'],
                proHidden: true,
            },
            {
                title: 'Teams', icon: 'pi pi-users',
                description: 'Group users into teams & departments',
                routerLink: ['/settings'], queryParams: { tab: 'users' },
                keywords: ['department', 'manager', 'restricted', 'collaborator'],
                proHidden: true,
            },
            {
                title: 'Permission Templates', icon: 'pi pi-shield',
                description: 'Screen-level access profiles per role',
                routerLink: ['/permission-templates'],
                keywords: ['access', 'role', 'readonly', 'menu', 'restrict'],
                proHidden: true,
            },
            {
                title: 'Commission Config', icon: 'pi pi-percentage',
                description: 'Partner levels & commission rules',
                routerLink: ['/commission'],
                keywords: ['business partner', 'associate', 'referral', 'payout'],
                proHidden: true,
            },
            {
                title: 'My Profile', icon: 'pi pi-user-edit',
                description: 'Your account, password & preferences',
                routerLink: ['/settings'], queryParams: { tab: 'profile' },
                keywords: ['password', 'avatar', 'email', 'account'],
            },
        ],
    },
    {
        label: 'Journey & Automation',
        icon: 'pi pi-compass',
        cards: [
            {
                title: 'Journey Phases', icon: 'pi pi-list',
                description: 'Phases, statuses & required fields per journey',
                routerLink: ['/settings'], queryParams: { tab: 'config' },
                keywords: ['phase', 'status', 'sla', 'sop', 'stage', 'required fields'],
                proHidden: true,
            },
            {
                title: 'Workflows', icon: 'pi pi-sitemap',
                description: 'Conversation flows & journey automations',
                routerLink: ['/workflows'],
                keywords: ['flow', 'bot', 'steps', 'automation'],
                proHidden: true,
            },
            {
                title: 'Operations', icon: 'pi pi-sliders-h',
                description: 'The journeys that govern your cases',
                routerLink: ['/operations'],
                keywords: ['journey', 'case', 'operation'],
                proHidden: true,
            },
            {
                title: 'Booking Config', icon: 'pi pi-calendar',
                description: 'Appointment types, slots & calendars',
                routerLink: ['/settings'], queryParams: { tab: 'booking' },
                keywords: ['appointment', 'slot', 'calendar', 'schedule'],
                proHidden: true,
            },
            {
                title: 'Stages', icon: 'pi pi-tag',
                description: 'Pipeline stages for your leads',
                routerLink: ['/settings'], queryParams: { tab: 'statuses' },
                keywords: ['pipeline', 'status', 'stage'],
                proOnly: true,
            },
            {
                title: 'Automation', icon: 'pi pi-bolt',
                description: 'Guided setup: persona, sources & auto-replies',
                routerLink: ['/settings'], queryParams: { tab: 'automation' },
                keywords: ['persona', 'auto reply', 'setup'],
                proOnly: true,
            },
            {
                title: 'Follow-up Reminders', icon: 'pi pi-bell',
                description: 'Per-stage follow-up cadence & stop rules',
                routerLink: ['/settings'], queryParams: { tab: 'reminders' },
                keywords: ['cadence', 'nudge', 'reminder'],
                proOnly: true,
            },
        ],
    },
    {
        label: 'Channels & Lead Sources',
        icon: 'pi pi-share-alt',
        cards: [
            {
                title: 'Channels', icon: 'pi pi-comments',
                description: 'WhatsApp, chatbot & email connections',
                routerLink: ['/settings'], queryParams: { tab: 'channels' },
                keywords: ['whatsapp', 'chatbot', 'email', 'smtp', 'widget', 'number'],
            },
            {
                title: 'Lead Sources', icon: 'pi pi-link',
                description: 'Meta, Google, IndiaMART, Shopify, webhooks & more',
                routerLink: ['/settings'], queryParams: { tab: 'sources' },
                keywords: ['meta', 'facebook', 'instagram', 'google ads', 'indiamart',
                           'shopify', 'webhook', 'website', 'form', 'integration'],
                proHidden: true,
            },
            {
                title: 'Shopify', icon: 'pi pi-shopping-cart',
                description: 'Connect your store — orders, customers & abandoned carts become leads',
                // ?source= (not ?connect=) — the sources tab's param handler filters on `source`.
                routerLink: ['/settings'], queryParams: { tab: 'sources', source: 'shopify' },
                keywords: ['shopify', 'store', 'ecommerce', 'orders', 'checkout', 'abandoned cart',
                           'webhook', 'connect', 'payment'],
                proHidden: true,
            },
            {
                title: 'Meta (Facebook & Instagram)', icon: 'pi pi-facebook',
                description: 'Guided setup — share your assets, map forms, route conversions, go live',
                routerLink: ['/meta-setup'],
                keywords: ['meta', 'facebook', 'instagram', 'lead ads', 'setup', 'wizard', 'connect',
                           'page', 'lead form', 'pixel', 'dataset', 'capi', 'share', 'business manager'],
                proHidden: true,
            },
            {
                title: 'Voice & Telephony', icon: 'pi pi-phone',
                description: 'Click to call, the smart dialer & inbound routing',
                routerLink: ['/voice-setup'],
                keywords: ['call', 'calling', 'phone', 'dialer', 'auto dialer', 'click to call',
                           'softphone', 'extension', 'ivr', 'inbound', 'outbound', 'recording',
                           'caller id', 'did', 'telephony', 'tata', 'smartflo', 'dnd'],
            },
            {
                title: 'Event Types', icon: 'pi pi-code',
                description: 'Map external app events to journey actions',
                routerLink: ['/event-mappings'],
                keywords: ['event', 'mapping', 'app event', 'pipeline'],
            },
        ],
    },
    {
        label: 'Data & Catalog',
        icon: 'pi pi-database',
        cards: [
            {
                title: 'Master Data', icon: 'pi pi-database',
                description: 'Reusable dropdown values & source names',
                routerLink: ['/settings'], queryParams: { tab: 'master-data' },
                keywords: ['dropdown', 'values', 'tags', 'source name', 'city'],
            },
            {
                title: 'Catalogs', icon: 'pi pi-sitemap',
                description: 'Products, services & price lists',
                routerLink: ['/catalogs'],
                keywords: ['product', 'price', 'quotation', 'cpq', 'item'],
            },
            {
                title: 'Activity Types', icon: 'pi pi-bookmark',
                description: 'What agents can log on a case',
                routerLink: ['/settings'], queryParams: { tab: 'activity-types' },
                keywords: ['activity', 'log', 'call', 'visit'],
            },
            {
                title: 'Proposal Templates', icon: 'pi pi-images',
                description: 'Quotation & proposal document designs',
                routerLink: ['/settings'], queryParams: { tab: 'proposals' },
                keywords: ['proposal', 'quotation', 'pdf', 'template', 'document'],
                proHidden: true,
            },
            {
                title: 'Numbering', icon: 'pi pi-hashtag',
                description: 'Number formats for quotations & documents, by condition',
                routerLink: ['/settings'], queryParams: { tab: 'numbering' },
                keywords: ['number', 'numbering', 'series', 'sequence', 'prefix', 'quotation', 'document', 'format'],
                proHidden: true,
            },
        ],
    },
];
